import { useEffect, useState } from 'react';
import { collection, collectionGroup, orderBy, query, where } from 'firebase/firestore';
import { useAuth } from '../auth/AuthProvider';
import { db } from '../lib/firebase';
import { listenQuery } from '../lib/liveRead';
import { canViewChargeFlags, type ChargeFlag } from '../types/models';

/** Job flags, or the company's open flags for Accounts. No reads for denied roles. */
export function useChargeFlags(jobId: string | undefined, enabled: boolean) {
  const {user, activeCompanyId, activeRole} = useAuth();
  const allowed = enabled && !!user && !!activeCompanyId && canViewChargeFlags(activeRole ?? undefined);
  const scope = JSON.stringify([user?.uid, activeCompanyId, activeRole, jobId, allowed]);
  const [attempt, retry] = useState(0);
  const [state, setState] = useState<{scope: string; flags: ChargeFlag[]; error: string | null} | null>(null);
  useEffect(() => {
    let active = true;
    setState(null);
    if (!allowed || !activeCompanyId) return;
    const target = jobId ? collection(db, 'companies', activeCompanyId, 'jobs', jobId, 'chargeFlags')
      : query(collectionGroup(db, 'chargeFlags'), where('companyId', '==', activeCompanyId), where('status', '==', 'open'), orderBy('flaggedAt', 'desc'));
    const stop = listenQuery(target, snap => {
      if (active) setState({scope, flags: snap.docs.map(d => ({id: d.id, ...d.data()} as ChargeFlag)), error: null});
    }, error => {if(active) setState({scope, flags: [], error: error.message});});
    return () => {active = false; stop();};
  }, [scope, attempt]);
  const current = state?.scope === scope ? state : null;
  return {flags: allowed ? current?.flags ?? [] : [], loading: allowed && !current,
    error: allowed ? current?.error ?? null : null, retry: () => retry(n => n + 1)};
}
